import {useCallback, useState} from 'react';
import {useIntl} from 'react-intl';
import classNames from 'classnames';
import {sendToStatist} from 'packages/statist';
import {type Editor} from '@tiptap/core';

import {
    CheckIcon,
    ChevronUpIcon,
    FormatHeaderNewIcon,
    FormatHeader1NewIcon,
    FormatHeader2NewIcon,
    FormatHeader3NewIcon,
} from '@time-webkit/all/icons';
import {DropdownListItem} from '@time-webkit/all/organisms';
import {PopoverOverlay} from '@time-webkit/all/molecules/popover';
import {KEYBOARD_SHORTCUTS} from 'components/keyboard_shortcuts/keyboard_shortcuts_sequence';

import {ActionButton} from '../action-button';
import {ShortcutTooltip} from '../shortcut-tooltip';
import {AVAILABLE_LEVELS, getHeadingLevelActive, type AvailableLevels} from '../../extensions/heading/heading-extension';

import styles from './styles.module.css';

type HeadingsProps = {
    editor?: Editor;
    disabled?: boolean;
}

const ICONS = {
    1: FormatHeader1NewIcon,
    2: FormatHeader2NewIcon,
    3: FormatHeader3NewIcon,
};

const TRANSLATIONS = {
    1: {id: 'editor.heading.level_1', defaultMessage: 'Big header'},
    2: {id: 'editor.heading.level_2', defaultMessage: 'Medium header'},
    3: {id: 'editor.heading.level_3', defaultMessage: 'Small header'},
};

const SHORTCUTS = {
    1: 'Ctrl Shift 1',
    2: 'Ctrl Shift 2',
    3: 'Ctrl Shift 3',
};

type HeadingListItemProps = {
    level: AvailableLevels[number];
    isActive: boolean;
    onClick: (level: AvailableLevels[number]) => void;
}

const HeadingListItem = (props: HeadingListItemProps) => {
    const {
        level,
        isActive,
        onClick,
    } = props;
    const {formatMessage} = useIntl();

    const Icon = ICONS[level];
    const translation = TRANSLATIONS[level];

    const handler = useCallback(() => {
        onClick(level);
    }, [level, onClick]);

    if (!Icon || !translation) {
        return null;
    }

    return (
        <DropdownListItem
            className={styles.headLevelItem}
            onClick={handler}
            icon={<span className={styles.icon}><Icon width={16} height={16}/></span>}
            label={formatMessage(translation)}
            iconRight={isActive && <CheckIcon className={styles.check} width={16} height={16} />}
            textRight={isActive ? '' : SHORTCUTS[level]}
            textRightClassName={styles.shortcut}
            aria-label={`Heading${level}`}
        />
    );
};

export const HeadingsComponent = ({editor, disabled}: HeadingsProps) => {
    const [show, setShow] = useState(false);
    const handlePopoverOpenChange = (isOpen: boolean) => {
        setShow(isOpen);
        if (!isOpen) {
            editor?.commands?.focus();
        }
    };

    const handleTogglePopover = useCallback(() => {
        setShow((prev) => !prev);
        sendToStatist('editor.format.tap', {formatType: 'header', hotkey: false});
    }, []);

    const handleHeaderClick = useCallback((level: AvailableLevels[number]) => {
        editor?.chain().focus().toggleHeading({level}).run();
        setShow(false);
        sendToStatist('editor.format.tap', {formatType: `header${level}`, hotkey: false});
    }, [editor]);

    const headingLevelActive = getHeadingLevelActive(editor);
    const HeadingIcon = (headingLevelActive && ICONS[headingLevelActive]) || FormatHeaderNewIcon;

    return (
        <PopoverOverlay
            isOpen={show}
            placement='top left'
            offset={4}
            containerPadding={8}
            onOpenChange={handlePopoverOpenChange}
            renderTriggerElement={(triggerProps, ref) => (
                <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownHeading} isHidden={disabled}>
                    <ActionButton
                        {...triggerProps}
                        ref={ref}
                        aria-label={'Heading'}
                        isActive={Boolean(headingLevelActive) || show}
                        isDisabled={disabled}
                        onPress={handleTogglePopover}
                        icon={
                            <div className={styles.buttons}>
                                <HeadingIcon />
                                <ChevronUpIcon className={classNames(styles.chevron, {[styles.transformed]: show})} />
                            </div>
                        }
                        data-qa='editor_button_heading'
                        data-testid='editor_button_heading'
                        buttonClassName={styles.buttonClassName}
                    />
                </ShortcutTooltip>
            )}
        >
            <div className={styles.headings}>
                {AVAILABLE_LEVELS.map((level) => (
                    <HeadingListItem
                        level={level}
                        key={level}
                        isActive={headingLevelActive === level}
                        onClick={handleHeaderClick}
                    />
                ))}
            </div>
        </PopoverOverlay>
    );
};
